import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

type WizardContextContent = { value: Record<string, undefined | number>; updater: (name: string, update: number | undefined) => void };
const WizardContext = createContext<WizardContextContent | undefined>(undefined);

type WizardContextProviderProps = { children: ReactNode };

const WizardOpeningProvider = ({ children }: WizardContextProviderProps) => {
  const [value, setValue] = useState<WizardContextContent['value']>({});

  const updater = useCallback((name: string, update: number | undefined) => {
    setValue((old) => ({ ...old, [name]: update }));
  }, []);

  const context = useMemo(
    () => ({
      value,
      updater,
    }),
    [updater, value],
  );

  return <WizardContext.Provider value={context}>{children}</WizardContext.Provider>;
};

export default WizardOpeningProvider;

export const useWizardOpening = (wizard: string, defaultStartStep?: number) => {
  const context = useContext(WizardContext);

  if (context === undefined) {
    throw new Error('useWizardContext must be used within a WizardContextProvider');
  }

  const startStep = useMemo(() => context.value[wizard] ?? defaultStartStep, [context.value, defaultStartStep, wizard]);
  const open = useCallback((step: number = 0) => context.updater(wizard, step), [context, wizard]);
  const close = useCallback(() => context.updater(wizard, undefined), [context, wizard]);

  return { startStep, open, close };
};
